import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle, Clock, Trophy } from 'lucide-react';
import { getUpcomingDeadlines } from '../services/api';

const DeadlineReminderModal = () => {
  const [open, setOpen] = useState(false);
  const [deadlines, setDeadlines] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const checkDeadlines = async () => {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      if (!user) return;

      try {
        const upcomingDeadlines = await getUpcomingDeadlines();
        const needsReminder = upcomingDeadlines.filter(d => d.needs_reminder);
        
        if (needsReminder.length > 0) {
          setDeadlines(needsReminder);
          
          // Check if we already showed reminder for these races today
          const shownToday = localStorage.getItem('reminder_shown_date');
          const today = new Date().toDateString();
          
          if (shownToday !== today) {
            setOpen(true);
            localStorage.setItem('reminder_shown_date', today);
          }
        }
      } catch (error) {
        console.error('Error checking deadlines:', error);
      }
    };

    // Small delay to let auth settle after login
    const timeout = setTimeout(checkDeadlines, 1000);
    
    // Check every hour
    const interval = setInterval(checkDeadlines, 3600000);
    
    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, []);

  const handleGoToPredict = () => {
    setOpen(false);
    navigate('/voorspel');
  };

  if (deadlines.length === 0) return null;

  // Separate race deadlines from championship reminder
  const raceDeadlines = deadlines.filter(d => !d.is_championship);
  const championshipReminder = deadlines.find(d => d.is_championship);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2 text-orange-600">
            <AlertTriangle size={24} />
            <span>Voorspelling Herinnering</span>
          </DialogTitle>
          <DialogDescription>
            {raceDeadlines.length > 0 
              ? 'Je hebt nog geen voorspelling ingediend voor de volgende race(s):'
              : 'Je hebt nog niet alle kampioenschapsvoorspellingen ingevuld:'
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-3 py-4">
          {/* Race deadlines */}
          {raceDeadlines.map((deadline) => (
            <div
              key={deadline.race_id}
              className="bg-[#FDB913]/10 border border-[#FDB913]/30 rounded-lg p-3"
            >
              <div className="font-semibold text-foreground">{deadline.race_name}</div>
              <div className="text-sm text-muted-foreground flex items-center mt-1">
                <Clock size={14} className="mr-1" />
                Deadline over {Math.round(deadline.hours_until_deadline)} uur
              </div>
            </div>
          ))}
          
          {/* Championship reminder */}
          {championshipReminder && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <div className="font-semibold text-foreground flex items-center">
                <Trophy size={16} className="mr-2 text-[#FDB913]" />
                {championshipReminder.race_name}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                Vul je WK en CC voorspellingen in voordat het seizoen begint!
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Later
          </Button>
          <Button
            className="bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911]"
            onClick={handleGoToPredict}
          >
            Voorspelling Maken
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeadlineReminderModal;
